import React, { useEffect, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const MEDAL = {
  1: { icon: '🥇', label: 'Top ID Hunter', color: '#f7d55c' },
  2: { icon: '🥈', label: 'ID Hunter', color: '#d0d0d0' },
  3: { icon: '🥉', label: 'ID Hunter', color: '#e0a36a' },
};

const cache = new Map();
export const fetchHunterStats = (userId) => {
  if (!userId) return Promise.resolve(null);
  if (!cache.has(userId)) {
    cache.set(userId, fetch(`${API}/api/users/${userId}/id-stats`).then(r => (r.ok ? r.json() : null)).catch(() => null));
  }
  return cache.get(userId);
};

const useHunterStats = (userId) => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let on = true;
    fetchHunterStats(userId).then(d => { if (on) setStats(d); });
    return () => { on = false; };
  }, [userId]);
  return stats;
};

// Inline "clips ID'd for others" chip — shown wherever a profile appears.
export const HunterChip = ({ userId, className = '' }) => {
  const stats = useHunterStats(userId);
  if (!stats) return null;
  const { hunted_tracks: hunted = 0, hunter_rank: rank, hunter_streak: streak = 0 } = stats;
  if (!hunted && !rank) return null;
  const m = rank ? MEDAL[rank] : null;
  return (
    <span data-testid={`hunter-chip-${userId}`} className={`inline-flex items-center gap-2 ${className}`}>
      <span
        title="Wall clips this BeatSeeker has ID'd for other people (all-time)"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
          padding: '3px 10px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 700,
          color: '#00e5ff', background: 'rgba(0,229,255,0.1)', border: '1px solid rgba(0,229,255,0.4)',
        }}
      >
        🎯 {hunted} hunted
      </span>
      {m && (
        <span
          data-testid={`hunter-chip-medal-${userId}`}
          title={`#${rank} on this month's Wall ID leaderboard${streak >= 2 ? ` · ${streak} months running` : ''}`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
            padding: '3px 10px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 700,
            color: m.color, background: `${m.color}14`, border: `1px solid ${m.color}66`,
          }}
        >
          {m.icon} {m.label}
          {streak >= 2 && <span style={{ color: '#ff9a3c', fontWeight: 800 }}>🔥{streak}</span>}
        </span>
      )}
    </span>
  );
};

// Full "ID Hunter Record" panel for profile pages.
export const HunterRecordCard = ({ userId }) => {
  const stats = useHunterStats(userId);
  if (!stats) return null;
  const { identified_tracks: identified = 0, hunted_tracks: hunted = 0, hunted_this_month: month = 0, hunter_rank: rank, hunter_streak: streak = 0 } = stats;
  const m = rank ? MEDAL[rank] : null;
  return (
    <div
      data-testid={`hunter-record-card-${userId}`}
      className="bg-gradient-to-br from-slate-800/90 via-fuchsia-900/20 to-slate-800/90 backdrop-blur-xl rounded-2xl p-6 border border-fuchsia-500/30 shadow-xl"
    >
      <h3 className="text-xl font-bold text-white mb-1 flex items-center gap-2 flex-wrap">
        <span className="text-fuchsia-400">🎯</span> ID Hunter Record
        {m && (
          <span
            data-testid={`hunter-record-medal-${userId}`}
            title={`#${rank} on this month's Wall ID leaderboard`}
            className="ml-1"
            style={{ fontSize: '0.75rem', fontWeight: 700, padding: '3px 10px', borderRadius: 999, color: m.color, background: `${m.color}14`, border: `1px solid ${m.color}66` }}
          >
            {m.icon} {m.label} · #{rank} this month{streak >= 2 ? ` · 🔥${streak}` : ''}
          </span>
        )}
      </h3>
      <p className="text-sm text-gray-400 mb-4">Tracks identified with the BEATSEEK engine vs. clips named for other BeatSeekers.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-slate-700/30 rounded-xl p-4 border border-cyan-500/20">
          <div className="text-3xl font-bold text-cyan-400" data-testid={`profile-identified-count-${userId}`}>{identified}</div>
          <div className="text-sm text-gray-400 mt-1">Identified Tracks</div>
        </div>
        <div className="bg-slate-700/30 rounded-xl p-4 border border-fuchsia-500/20">
          <div className="text-3xl font-bold text-fuchsia-400" data-testid={`profile-hunted-count-${userId}`}>{hunted}</div>
          <div className="text-sm text-gray-400 mt-1">Hunted for Others</div>
        </div>
        <div className="bg-slate-700/30 rounded-xl p-4 border border-amber-500/20">
          <div className="text-3xl font-bold text-amber-400" data-testid={`profile-hunted-month-${userId}`}>{month}</div>
          <div className="text-sm text-gray-400 mt-1">Hunted This Month</div>
        </div>
      </div>
    </div>
  );
};

export default HunterChip;
